// src/App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";
import { TENANT_ID } from "./tenant";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import LineAuthCallback from "./features/auth/LineAuthCallback";
import Dashboard from "./features/dashboard/Dashboard";
import PublicHomePage from "./features/public/PublicHomePage";
import Loader from "./components/Loader";

// ホーム(会員/未ログイン)と LINE ログインの着地点以外は、最初のバンドルから外す。
// ⚠/auth/line は LINE webview で読み込みが詰まりやすい経路なので、追加の取得を挟まない(eager のまま)。
const AuthScreen = lazy(() => import("./features/auth/AuthScreen"));
const GiftReceivePage = lazy(() => import("./features/dashboard/GiftReceivePage"));
const ReservationInviteReceivePage = lazy(() =>
  import("./features/dashboard/ReservationInviteReceivePage")
);
const ProfileCompletionPage = lazy(() => import("./pages/ProfileCompletionPage"));

/* ================================
   Tenant Guard Hook
================================ */
// ⚠config/app は誰でも読める(Core の firestore.rules)。以前は Auth の初期化完了を待ってから
//   読んでいたため「Authの初期化(保存済みログインの再確認＝通信2本)→この読み取り」が直列になり、
//   LINE から開いたときの待ちが伸びていた。今は起動と同時に読み、前回 ok だったテナントは
//   待たずに画面を出す(読み取り結果は裏で反映する)。
const TENANT_OK_KEY = "spaceos_tenant_ok_v1";

const wasTenantOk = (tenantId) => {
  try {
    return window.localStorage.getItem(TENANT_OK_KEY) === tenantId;
  } catch (_e) {
    return false;
  }
};

function useTenantGuard(tenantId) {
  const [status, setStatus] = useState(() =>
    wasTenantOk(tenantId) ? "ok" : "loading"
  );

  useEffect(() => {
    let alive = true;

    const check = async () => {
      try {
        const snap = await getDoc(
          doc(db, "tenants", tenantId, "config", "app")
        );
        if (!alive) return;
        try {
          if (snap.exists()) window.localStorage.setItem(TENANT_OK_KEY, tenantId);
          else window.localStorage.removeItem(TENANT_OK_KEY);
        } catch (_e) {
          /* noop */
        }
        setStatus(snap.exists() ? "ok" : "notfound");
      } catch (e) {
        console.error("[tenantGuard] error:", e);
        // 前回 ok で画面を出しているなら、通信の失敗で「ご利用いただけません」に倒さない
        if (alive && !wasTenantOk(tenantId)) setStatus("notfound");
      }
    };

    check();
    return () => {
      alive = false;
    };
  }, [tenantId]);

  return status;
}

/* ================================
   Tenant Not Found UI
================================ */
function TenantNotFound() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h2>この施設は現在ご利用いただけません</h2>
      <p>URLをご確認ください。</p>
    </div>
  );
}

/* ================================
   LINE内ブラウザの描画詰まり対策
   ローディング明けに合成を数回起こす。LINE webview は稀に「内容は用意できたのに
   ユーザーがスクロールするまで画面に反映されない」状態になる(会員証LIFFで実証済み)。
   常時揺らすとちらつくので、切替直後の時間差数発に限定する。
================================ */
function kickWebviewPaint() {
  const kick = () => {
    try {
      const el = document.getElementById("root") || document.body;
      void el.offsetHeight; // reflow

      // ⚠ここで transform を使ってはいけない。
      //   transform の付いた祖先は position:fixed の「包含ブロック」になる(CSS仕様)。
      //   #root は中身が fixed のオーバーレイだけなので高さ0で、包含ブロックになると
      //   `fixed inset-0` が高さ0に潰れ、中の overflow-y:auto が全部クリップされる
      //   ＝ログイン画面やモーダルが**真っ白**になる。
      //   2026-10-01 に dev/prod 両方で発生を確認（/auth/start が空白。DOMには在るが描画されない）。
      //   元の実装は el.style.transform を "translateZ(0)" と比較していたが、ブラウザは
      //   インラインstyleを "translateZ(0px)" に正規化するため比較が永久に一致せず、
      //   トグルのつもりが「付けっぱなし」になっていた。
      //   opacity はスタッキングコンテキストを作るだけでレイアウトに影響しないので安全。
      el.style.opacity = "0.999";
      void el.offsetHeight;

      // 元に戻す。rAF は「1フレーム描かせてから戻す」ため、setTimeout は
      // 非表示タブ(rAFが走らない)で付けっぱなしにならないための保険。
      // 二重に呼ばれても害は無い。
      const clear = () => {
        try {
          el.style.opacity = "";
        } catch (_e2) {
          /* noop */
        }
      };
      requestAnimationFrame(clear);
      setTimeout(clear, 300);
    } catch (_e) {
      /* noop */
    }
  };
  [0, 150, 500, 1200, 2500, 5000].forEach((ms) => setTimeout(kick, ms));
}

/* ================================
   App
================================ */
export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const tenantStatus = useTenantGuard(TENANT_ID);

  const handleAuthComplete = (u) => {
    setUser(u ?? null);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u ?? null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // ローディング明けの一瞬に描画が詰まる対策(LINE内ブラウザ)。
  const appReady = authReady && tenantStatus === "ok";
  useEffect(() => {
    if (appReady) kickWebviewPaint();
  }, [appReady]);

  // LIFF の着地点(/auth/line)だけは、テナント設定の読み込みを待たずに描画する。
  // ⚠ここを待たせると、LINEアプリ内ブラウザで Firestore の読み取りが詰まったときに
  //   「ローディングのまま進まない」になり、画面に手がかりが何も出ない。
  //   この画面はテナントIDをホスト名から決めるので、config/app の読み取りを必要としない。
  const isLineAuthLanding =
    typeof window !== "undefined" && window.location.pathname === "/auth/line";

  if (!isLineAuthLanding && (tenantStatus === "loading" || !authReady)) {
    // public/index.html の先出し表示と同じ見た目(バンドル到着の前後で画面が変わらないように)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500 text-[13px] font-bold">
        <Loader label="読み込み中…" />
      </div>
    );
  }

  if (!isLineAuthLanding && tenantStatus === "notfound") {
    return <TenantNotFound />;
  }

  return (
    <>
      <div
        id="recaptcha-container"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      />

      <BrowserRouter>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500 text-[13px] font-bold">
              <Loader label="読み込み中…" />
            </div>
          }
        >
        <Routes>
          {/* LIFF のエンドポイント。ログイン前でも後でも通るので user で分岐しない
              （LINEアプリ内ブラウザには外部ブラウザのセッションが無い） */}
          <Route path="/auth/line" element={<LineAuthCallback />} />

          <Route
            path="/auth/start"
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <AuthScreen
                  onAuthComplete={handleAuthComplete}
                  onCancel={() => window.history.length > 1 ? window.history.back() : null}
                />
              )
            }
          />

          <Route
            path="/gift/receive/:token"
            element={<GiftReceivePage />}
          />

          <Route
            path="/invite/reservation/:token"
            element={<ReservationInviteReceivePage />}
          />

          <Route
            path="/profile/complete"
            element={
              user ? (
                <ProfileCompletionPage />
              ) : (
                <AuthScreen
                  onAuthComplete={handleAuthComplete}
                  redirectTo="/profile/complete"
                />
              )
            }
          />

          <Route
            path="/*"
            element={
              user ? (
                <Dashboard user={user} />
              ) : (
                <PublicHomePage />
              )
            }
          />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}
/*
import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";
import { TENANT_ID } from "./tenant";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import AuthScreen from "./features/auth/AuthScreen";
import Dashboard from "./features/dashboard/Dashboard";
import GiftReceivePage from "./features/dashboard/GiftReceivePage";
import ReservationInviteReceivePage from "./features/dashboard/ReservationInviteReceivePage";
import Loader from "./components/Loader";


function useTenantGuard(tenantId, authReady) {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!authReady) return; // 🔥 ここ重要

    const check = async () => {
      try {
        const snap = await getDoc(
          doc(db, "tenants", tenantId, "config", "app")
        );
        setStatus(snap.exists() ? "ok" : "notfound");
      } catch (e) {
        console.error("[tenantGuard] error:", e);
        setStatus("notfound");
      }
    };

    check();
  }, [tenantId, authReady]);

  return status;
}


function TenantNotFound() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h2>この施設は現在ご利用いただけません</h2>
      <p>URLをご確認ください。</p>
    </div>
  );
}


export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // 👇 ここで guard を呼ぶ（コンポーネント内）
  const tenantStatus = useTenantGuard(TENANT_ID, authReady);

  const handleAuthComplete = (u) => setUser(u ?? null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u ?? null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // tenant loading
  if (tenantStatus === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // tenant not found
  if (tenantStatus === "notfound") {
    return <TenantNotFound />;
  }

  // auth loading
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>

      <div
        id="recaptcha-container"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          pointerEvents: "none"
        }}
      >

      </div>

      <BrowserRouter>
        <Routes>
          <Route path="/gift/receive/:token" element={<GiftReceivePage />} />
          <Route
            path="/invite/reservation/:token"
            element={<ReservationInviteReceivePage />}
          />

          <Route
            path="/*"
            element={
              !user ? (
                <AuthScreen onAuthComplete={handleAuthComplete} />
              ) : (
                <Dashboard user={user} />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </>
  );
}
*/